'use client'

import { usePathname, useRouter } from '@/i18n/routing'
import { startTransition } from 'react'
import { useParams } from 'next/navigation'
import { LanguageSelector as LanguageSelectorComponent, GlobeIcon } from '@ccpgames/eve-web-core'
import type { LanguageOption } from '@ccpgames/eve-web-core'
import classes from './LanguageSelector.module.css'

interface LanguageSelectorProps {
  currentLocale: string
  flatList?: boolean
}

const LanguageList: LanguageOption[] = [
  { locale: 'en', label: 'English' },
  { locale: 'fr', label: 'Français' },
  { locale: 'de', label: 'Deutsch' },
  { locale: 'es', label: 'Español' },
  { locale: 'ja', label: '日本語' },
  { locale: 'ko', label: '한국어' },
  { locale: 'ru', label: 'Русский' },
  { locale: 'zh', label: '简体中文' },
]

export function LanguageSelector({ currentLocale, flatList = false }: LanguageSelectorProps) {
  const router = useRouter()

  const params = useParams()
  const pathname = usePathname()

  // Sets the locale cookie if user clicks on a language
  // TODO: We might not need this, when a locale route is requested, the router package handles setting the cookie.
  const handleClick = (languageLocale: string) => {
    startTransition(() => {
      router.replace(
        // @ts-expect-error -- TypeScript will validate that only known `params`
        // are used in combination with a given `pathname`. Since the two will
        // always match for the current route, we can skip runtime checks.
        { pathname, params },
        { locale: languageLocale },
      )
    })
  }

  if (flatList) {
    return (
      <div className={classes.localeSwitcher}>
        <span className={classes.localeSwitcherIcon}>
          <GlobeIcon />
        </span>
        {LanguageList.map((language) => {
          const isActive = currentLocale === language.locale
          const itemClassName = [classes.localeSwitcherItem, isActive ? classes.localeSwitcherItemActive : '']
            .filter(Boolean)
            .join(' ')
          return (
            <button
              type="button"
              key={language.locale}
              className={itemClassName}
              aria-current={isActive ? 'true' : undefined}
              onClick={() => handleClick(language.locale)}
            >
              {language.locale}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <LanguageSelectorComponent
      locales={LanguageList}
      icon={<GlobeIcon />}
      activeLocale={currentLocale}
      highlightColor="#5CCBCB"
      onLocaleSelect={handleClick}
    />
  )
}

export default LanguageSelector
